import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * Deep-link from a push notification: when the URL has `?alert=<id>`, look
 * up that alert in the feed and trigger handleAlertFocus (map fly-to,
 * popup auto-open, feed-card highlight). The query param is then cleared so
 * refreshing the page doesn't re-trigger the animation. We retain the param
 * until the alert is found (it may not be loaded yet on first paint).
 */
export function useDeepLinkAlert({ alerts, searchParams, setSearchParams, handleAlertFocus }) {
  const consumedDeepLinkRef = useRef(null);
  const deepLinkTimerRef = useRef(null);

  useEffect(() => {
    const targetId = searchParams.get("alert");
    if (!targetId) {
      consumedDeepLinkRef.current = null;
      if (deepLinkTimerRef.current) {
        clearTimeout(deepLinkTimerRef.current);
        deepLinkTimerRef.current = null;
      }
      return;
    }
    if (consumedDeepLinkRef.current === targetId) return;
    if (Array.isArray(alerts) && alerts.length > 0) {
      const match = alerts.find((a) => a.alert_id === targetId);
      if (match) {
        consumedDeepLinkRef.current = targetId;
        if (deepLinkTimerRef.current) {
          clearTimeout(deepLinkTimerRef.current);
          deepLinkTimerRef.current = null;
        }
        handleAlertFocus(match);
        const next = new URLSearchParams(searchParams);
        next.delete("alert");
        setSearchParams(next, { replace: true });
        toast.success("Opened alert from notification");
        return;
      }
    }
    // Alert not in active list (yet). Arm a 6-second grace timer; if it
    // still isn't there, the alert has likely been resolved/deleted, so we
    // tell the user and clear the URL so refresh doesn't loop.
    if (!deepLinkTimerRef.current) {
      deepLinkTimerRef.current = setTimeout(() => {
        if (consumedDeepLinkRef.current === targetId) return;
        consumedDeepLinkRef.current = targetId;
        toast.message("That alert is no longer active");
        const next = new URLSearchParams(searchParams);
        next.delete("alert");
        setSearchParams(next, { replace: true });
      }, 6000);
    }
  }, [alerts, searchParams, setSearchParams, handleAlertFocus]);
}
